import IconAstronaut3 from '@apollo/icons/default/IconAstronaut3.svg';
import IconDocument from '@apollo/icons/default/IconDocument.svg';
import IconFilter from '@apollo/icons/default/IconFilter.svg';
import IconHeadset from '@apollo/icons/default/IconHeadset.svg';
import IconHelp from '@apollo/icons/default/IconHelp.svg';
import IconLock from '@apollo/icons/default/IconLock.svg';
import IconLogout from '@apollo/icons/default/IconLogout.svg';
import IconMegaphone from '@apollo/icons/default/IconMegaphone.svg';
import IconMetrics from '@apollo/icons/default/IconMetrics.svg';
import IconPlanet4 from '@apollo/icons/default/IconPlanet4.svg';
import IconSandbox from '@apollo/icons/default/IconSandbox.svg';
import IconSupport from '@apollo/icons/default/IconSupport.svg';
import {
  ApolloLogo,
  Avatar,
  Button,
  IconButton,
  Menu,
  MenuButton,
  MenuContent,
  MenuItem,
  MenuList,
  Spinner,
} from '@apollo/orbit';
import classnames from 'classnames';
import type { ReactNode } from 'react';
import React from 'react';
import { Link, matchPath, useLocation } from 'react-router-dom';

import * as orgRoutes from 'src/app/account/routes';
import { isEmbeddableExplorerRoute } from 'src/app/embeddableExplorer/isEmbeddableExplorerRoute';
import { isEmbeddableSandboxRoute } from 'src/app/embeddableSandbox/isEmbeddableSandboxRoute';
import { MaybeEmbeddedLoginLink } from 'src/app/embedHelpers/MaybeEmbedLoginLink';
import { OutlinkForEmbed } from 'src/app/embedHelpers/OutlinkForEmbed';
import { graphRefToString, useGraphRef } from 'src/app/graph/hooks/useGraphRef';
import {
  sandboxExplorerRouteConfig,
  sandboxGraphRouteConfig,
} from 'src/app/graph/routes';
import {
  embedLogoutRouteConfig,
  logoutRouteConfig,
} from 'src/app/onboarding/routes';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { useGetContactSupportOverlayLocation } from 'src/components/ContactSupportCTAs';
import { useAuth } from 'src/context/AuthContext';
import { useIsAppOfflineContext } from 'src/hooks/useIsAppOffline';
import Config from 'src/lib/config';
import { DocsRouter } from 'src/lib/routers';
import { mergeQueryParams } from 'src/lib/routing';
import { useNewWorkerVersionAvailable } from 'src/serviceWorkerSetup/serviceWorkerReactiveVariables';
import { triggerWorkerUpdateAndReload } from 'src/serviceWorkerSetup/serviceWorkerRegistration';

import { ChangelogModalTrigger } from '../changelogModalTrigger/ChangelogModalTrigger';

import { OwnersContactMenu } from './OwnersContactMenu';
import { SudoToggle } from './sudoToggle/SudoToggle';

/**
 * Component that renders the user menu items
 */
const AvatarMenuContent = ({
  newVersionAvailable,
  isLoggedIn,
  isInSandbox,
}: {
  newVersionAvailable: boolean;
  isLoggedIn: boolean;
  isInSandbox: boolean;
}) => {
  const graphRef = useGraphRef();
  return (
    <MenuContent>
      <MenuList>
        {newVersionAvailable && (
          <MenuItem onClick={triggerWorkerUpdateAndReload}>
            Update available, click to relaunch
          </MenuItem>
        )}
        <MenuItem
          as={OutlinkForEmbed}
          to={orgRoutes.userSettingsRouteConfig.location({})}
          icon={<IconAstronaut3 />}
        >
          Personal Settings
        </MenuItem>
        <MenuItem
          as={OutlinkForEmbed}
          to={orgRoutes.userSettingsRouteConfig.location({
            hash: '#preview-features',
          })}
          icon={<IconFilter />}
        >
          Preview features
        </MenuItem>
        {
          // if a user is not logged in, they should only see the log in CTA
          // if a user is on the embedded explorer, they shouldn't see sandbox
          isLoggedIn && !isEmbeddableExplorerRoute() && (
            <MenuItem
              as={OutlinkForEmbed}
              to={isInSandbox ? '/' : sandboxExplorerRouteConfig.location({})}
              icon={<IconSandbox />}
              className="flex sm:hidden"
            >
              Sandbox
            </MenuItem>
          )
        }
        <MenuItem
          as={Link}
          to={
            isEmbeddableExplorerRoute() || isEmbeddableSandboxRoute()
              ? embedLogoutRouteConfig.location({
                  from: `${window.location.pathname}${window.location.search}`,
                  graphRef: graphRefToString(graphRef) ?? undefined,
                })
              : logoutRouteConfig.location({})
          }
          icon={<IconLogout />}
        >
          Logout
        </MenuItem>
      </MenuList>
    </MenuContent>
  );
};

/**
 * Component that renders the support menu items
 */
const SupportMenuContent = ({
  isAuthenticated,
  location,
}: {
  isAuthenticated: boolean;
  location: ReturnType<typeof useLocation>;
}) => {
  const contactSupportOverlayLocation = useGetContactSupportOverlayLocation();
  return (
    <MenuContent>
      <MenuList>
        <MenuItem
          icon={<IconSupport />}
          as="a"
          href="https://support.apollographql.com/"
          rel="noopener noreferrer"
          target="_blank"
        >
          Help Center
        </MenuItem>

        {isAuthenticated && (
          <MenuItem
            icon={<IconHeadset />}
            as={Link}
            to={contactSupportOverlayLocation}
          >
            Contact Support
          </MenuItem>
        )}

        <MenuItem
          as="a"
          href="https://www.apollographql.com/trust/"
          rel="noopener noreferrer"
          target="_blank"
          icon={<IconLock />}
        >
          Apollo Trust and Security
        </MenuItem>

        <MenuItem
          as="a"
          href="https://community.apollographql.com"
          rel="noopener noreferrer"
          target="_blank"
          icon={<IconPlanet4 />}
        >
          Community Forum
        </MenuItem>

        <MenuItem
          as="a"
          href={DocsRouter.path('StudioHome')}
          rel="noopener noreferrer"
          target="_blank"
          icon={<IconDocument />}
        >
          Documentation
        </MenuItem>

        <MenuItem
          icon={<IconMetrics />}
          as="a"
          href="https://status.apollographql.com"
          rel="noopener noreferrer"
          target="_blank"
        >
          Status Report
        </MenuItem>
        <MenuItem
          as={Link}
          className="p-0 sm:hidden"
          to={{
            ...location,
            search: mergeQueryParams(location.search, {
              [Config.queryParameters.Overlay]: Config.modals.appChangelog,
            }),
          }}
          aria-label="What's new in Studio"
          icon={<IconMegaphone />}
        >
          What's new in Studio
        </MenuItem>
      </MenuList>
    </MenuContent>
  );
};

export const GlobalHeader = ({
  children,
  goHomeLink,
  leftNavContext,
}: {
  children?: ReactNode;
  leftNavContext?: 'collapsed' | 'expanded' | null;
  goHomeLink?: string;
}) => {
  const isOffline = useIsAppOfflineContext();
  const location = useLocation();
  const { isLoggedIn, user, loading } = useAuth();

  const isInSandbox = matchPath(
    location.pathname,
    sandboxGraphRouteConfig.definition,
  );

  const newWorkerVersionAvailable = useNewWorkerVersionAvailable();

  return (
    <>
      <header
        className="z-50 flex h-16 items-center gap-2 border-b border-primary bg-primary pr-2 text-secondary transition-background"
        aria-label="Global header"
      >
        <Tooltip placement="bottom" label="Go to graph index">
          <OutlinkForEmbed
            to={goHomeLink ?? '/'}
            className={classnames(
              'flex h-full flex-none items-center text-primary',
              !!leftNavContext && 'border-r border-primary',
              leftNavContext === 'collapsed'
                ? 'w-[5.5rem] justify-center'
                : 'w-[212px] justify-center px-2',
            )}
          >
            {leftNavContext === 'collapsed' ? (
              <ApolloLogo
                logoType="symbol"
                className="mx-[5px] size-10 [&_path:last-of-type]:hover:origin-center [&_path:last-of-type]:hover:animate-custom-spin"
              />
            ) : leftNavContext === 'expanded' ? (
              <ApolloLogo logoType="wordmark" className="mr-4 h-16 w-32 px-2" />
            ) : (
              <ApolloLogo logoType="wordmark" className="h-16 w-32 px-2" />
            )}
          </OutlinkForEmbed>
        </Tooltip>
        {children}
        <div className="flex-1" />
        {user?.__typename === 'User' && user.internalAdminRole && (
          <>
            <OwnersContactMenu />
            <SudoToggle adminRole={user.internalAdminRole} />
          </>
        )}
        {
          // if a user is not logged in, they should only see the log in CTA
          // if a user is on the embedded explorer, they shouldn't see sandbox
          isLoggedIn && !isEmbeddableExplorerRoute() && (
            <Tooltip label={isInSandbox ? 'Leave Sandbox' : 'Open Sandbox'}>
              <IconButton
                className="hidden sm:inline-flex"
                variant={isInSandbox ? 'primary' : 'hidden'}
                size="sm"
                icon={<IconSandbox className="size-4" />}
                as={OutlinkForEmbed}
                to={
                  isInSandbox
                    ? '/'
                    : sandboxExplorerRouteConfig.location({}) || '/'
                }
                aria-label={isInSandbox ? 'Leave Sandbox' : 'Open Sandbox'}
              />
            </Tooltip>
          )
        }
        <ChangelogModalTrigger
          className="hidden sm:inline-flex"
          newVersionAvailable={newWorkerVersionAvailable}
        />
        <Menu placement="bottom-end" isLazy>
          <MenuButton
            as={IconButton}
            aria-label="support menu"
            variant="hidden"
            icon={<IconHelp />}
            size="sm"
          />
          <SupportMenuContent
            isAuthenticated={user?.__typename === 'User' && !!user}
            location={location}
          />
        </Menu>
        {loading || (user?.__typename === 'User' && user) ? (
          <Menu placement="bottom-end" isLazy>
            <MenuButton
              aria-label="User menu settings"
              as={IconButton}
              variant="hidden"
              className="p-0"
              data-testid="login-button"
              size="sm"
            >
              {loading ? (
                <Spinner size="tiny" />
              ) : user?.__typename === 'User' ? (
                <Avatar
                  src={user.avatarUrl ?? undefined}
                  name={user.fullName}
                  size="xs"
                />
              ) : (
                <Avatar size="xs" />
              )}
            </MenuButton>

            <AvatarMenuContent
              newVersionAvailable={newWorkerVersionAvailable}
              isLoggedIn={isLoggedIn}
              isInSandbox={!!isInSandbox}
            />
          </Menu>
        ) : isOffline ? (
          <Button
            variant="primary"
            className="pointer-events-none"
            aria-label="Offline"
            size="sm"
          >
            Offline
          </Button>
        ) : (
          <Button
            className="min-w-0"
            variant="primary"
            as={MaybeEmbeddedLoginLink}
            userSegmentType="sandbox"
            aria-label="Log in"
            size="sm"
          >
            Log in
          </Button>
        )}
      </header>
    </>
  );
};
